import React, { useEffect, useState } from 'react'
import { ImageUpload } from '../../../../components/ImageUpload/ImageUpload'
import { type ImageUploadLabels, type UploadProgressCallback } from '../../../../components/ImageUpload/models'
import { useAssetFileUpload } from '../../../../components/ImageUpload/useAssetFileUpload'
import { useUserHasScopedBlockAccess } from '../../../../components/ScopedBlock/hooks'
import { EnterpriseManagedBanner } from '../EnterpriseManagedBanner'
import { FileKind } from '../../../../models/File'
import { useResetLogoFileMutation, useUpdateLogoFileMutation } from '../../../../store/account/account'
import { useLazyDownloadGlobalFileRequestQuery } from '../../../../store/media/file'
import { useAccount } from '../../../../common/useAccount'
import { BRANDING_ACCOUNT_SCOPE, BRANDING_LOGO_RESET_KEY, BRANDING_LOGO_UPDATE_KEY } from './brandingConstants'
import ZocksLogoFullBlack from '../../../../assets/images/ZocksLogoFullBlack.png'

const LOGO_MIN_HEIGHT = 320
const LOGO_MAX_DIMENSION = 2000
const _5_MB_IN_BYTES = 5 * 1024 * 1024
const ACCEPTED_TYPES = ['image/png', 'image/jpeg']

const LABELS: ImageUploadLabels = {
  drop: 'Drop a new logo here',
  hint: `PNG or JPG. At least ${LOGO_MIN_HEIGHT} px tall. Max 5 MB.`,
  change: 'Change logo',
  remove: 'Reset to default',
  removing: 'Resetting logo...',
  removeFailed: 'Could not reset the logo',
  wrongFormat: 'Only PNG or JPG images are supported.',
  tooLarge: 'Please upload an image under 5 MB.',
  cropTitle: 'Adjust logo',
  cropSave: 'Save logo',
}

interface BrandingLogoSectionProps {
  disabled?: boolean
}

export const BrandingLogoSection: React.FC<BrandingLogoSectionProps> = ({ disabled = false }) => {
  const { accountId, account } = useAccount()
  const { userHasScopedBlockAccess } = useUserHasScopedBlockAccess()
  const [canEdit] = userHasScopedBlockAccess([], undefined, BRANDING_ACCOUNT_SCOPE)

  const [updateLogoFile] = useUpdateLogoFileMutation({ fixedCacheKey: BRANDING_LOGO_UPDATE_KEY })
  const [resetLogoFile] = useResetLogoFileMutation({ fixedCacheKey: BRANDING_LOGO_RESET_KEY })
  const [downloadFileRequest] = useLazyDownloadGlobalFileRequestQuery()
  const uploadAsset = useAssetFileUpload(accountId, FileKind.LOGO_ASSET)

  const [resolved, setResolved] = useState<{ fileId: string; url: string | null } | null>(null)

  const isLogoEnforced = account?.enterpriseConfig.assets?.logo?.logoFile != null
  // Single source of truth: the IAM account, which both logo mutations refetch on success. Falling
  // back to the update mutation's own response would shadow fresher state (e.g. a later reset).
  const logoFileInfo = account?.effectiveConfig.assets?.logo?.logoFile ?? null
  const logoFileId = logoFileInfo?.fileId

  useEffect(() => {
    if (!logoFileId) {
      return
    }
    let active = true
    downloadFileRequest({ accountId, fileId: logoFileId })
      .unwrap()
      .then(({ data }) => {
        if (active) {
          setResolved({ fileId: logoFileId, url: data.location })
        }
      })
      .catch(() => {
        if (active) {
          setResolved({ fileId: logoFileId, url: null })
        }
      })
    return () => {
      active = false
    }
  }, [accountId, logoFileId, downloadFileRequest])

  const handleUpload = async (file: File, onProgress: UploadProgressCallback) => {
    const { fileId } = await uploadAsset(file, onProgress)
    await updateLogoFile({ accountId, body: { fileId } }).unwrap()
  }

  const handleReset = async () => {
    await resetLogoFile({ accountId }).unwrap()
  }

  return (
    <div className='d-flex flex-column zocks-gap-1'>
      <div className='d-flex flex-column'>
        <h6 className='zocks-text-gray-900'>Logo</h6>
        <p className='paragraph-small zocks-text-gray-700 m-0'>Shown to clients on pages and exported documents.</p>
      </div>
      {isLogoEnforced ? <EnterpriseManagedBanner message='Account logo is managed at the enterprise level.' /> : null}
      <ImageUpload
        imageUrl={resolved && resolved.fileId === logoFileId ? resolved.url : null}
        imageAlt='Account logo'
        shape='rectangle'
        placeholder={<img src={ZocksLogoFullBlack} alt='Default logo' />}
        acceptedTypes={ACCEPTED_TYPES}
        maxSize={_5_MB_IN_BYTES}
        minHeight={LOGO_MIN_HEIGHT}
        maxDimension={LOGO_MAX_DIMENSION}
        labels={LABELS}
        loading={logoFileId != null && resolved?.fileId !== logoFileId}
        disabled={disabled || !account || isLogoEnforced || !canEdit}
        onUpload={handleUpload}
        onRemove={!account || isLogoEnforced || !canEdit ? undefined : handleReset}
      />
    </div>
  )
}
